// Pending duplicate detections arrive as pairs. When several ideas are
// duplicates of each other ({#6, #7, #10, #12}) the pairs overlap, so the web
// shows them as one group: the pairs are joined on shared ideas and the group
// is resolved in one action. Kept free of API imports so the Worker test suite
// can run it.

/** The fields of a pending detection (api.ts DuplicateDetection) used here. */
export interface DuplicatePair {
  id: number;
  idea_id: number;
  duplicate_of_id: number;
  similarity_score: number;
  idea_number: number;
  duplicate_number: number;
  idea_title: string;
  duplicate_title: string;
  idea_body: string | null;
  duplicate_body: string | null;
  idea_status: string;
  duplicate_status: string;
  idea_source: string;
  duplicate_source: string;
  idea_created_at: string;
  duplicate_created_at: string;
  created_at: string;
}

export interface DuplicateGroupMember {
  id: number;
  number: number;
  title: string;
  body: string | null;
  status: string;
  source: string;
  created_at: string;
}

export interface DuplicateGroup<T extends DuplicatePair = DuplicatePair> {
  /** Stable while the group's pairs don't change: built from the lowest detection id. */
  key: string;
  /** The idea every other member merges into: the newest (highest id), as in a pair merge. */
  keeper: DuplicateGroupMember;
  /** Keeper first, then the rest newest → oldest. */
  members: DuplicateGroupMember[];
  detections: T[];
  topScore: number;
  /** When the most recent pair in the group was detected. */
  detectedAt: string;
}

export function groupDuplicates<T extends DuplicatePair>(pairs: T[]): DuplicateGroup<T>[] {
  const parent = new Map<number, number>();
  const find = (x: number): number => {
    let root = x;
    while (parent.get(root) !== root) root = parent.get(root)!;
    // Path compression keeps repeated lookups flat.
    while (x !== root) {
      const next = parent.get(x)!;
      parent.set(x, root);
      x = next;
    }
    return root;
  };

  const members = new Map<number, DuplicateGroupMember>();
  for (const p of pairs) {
    if (!parent.has(p.idea_id)) parent.set(p.idea_id, p.idea_id);
    if (!parent.has(p.duplicate_of_id)) parent.set(p.duplicate_of_id, p.duplicate_of_id);
    const a = find(p.idea_id);
    const b = find(p.duplicate_of_id);
    if (a !== b) parent.set(a, b);
    members.set(p.idea_id, {
      id: p.idea_id, number: p.idea_number, title: p.idea_title, body: p.idea_body,
      status: p.idea_status, source: p.idea_source, created_at: p.idea_created_at,
    });
    members.set(p.duplicate_of_id, {
      id: p.duplicate_of_id, number: p.duplicate_number, title: p.duplicate_title,
      body: p.duplicate_body, status: p.duplicate_status, source: p.duplicate_source,
      created_at: p.duplicate_created_at,
    });
  }

  const byRoot = new Map<number, T[]>();
  for (const p of pairs) {
    const root = find(p.idea_id);
    const list = byRoot.get(root);
    if (list) list.push(p);
    else byRoot.set(root, [p]);
  }

  const groups: DuplicateGroup<T>[] = [];
  for (const detections of byRoot.values()) {
    const ids = new Set<number>();
    for (const d of detections) {
      ids.add(d.idea_id);
      ids.add(d.duplicate_of_id);
    }
    const sorted = [...ids].sort((a, b) => b - a).map((id) => members.get(id)!);
    groups.push({
      key: `dupgroup-${Math.min(...detections.map((d) => d.id))}`,
      keeper: sorted[0]!, // every group has at least two members
      members: sorted,
      detections,
      topScore: Math.max(...detections.map((d) => d.similarity_score)),
      detectedAt: detections.reduce((max, d) => (d.created_at > max ? d.created_at : max), ''),
    });
  }
  // Strongest match first, like the pair list the API returns.
  return groups.sort((a, b) => b.topScore - a.topScore || a.key.localeCompare(b.key));
}

/** Detections in `group` that involve `memberId` — dismissing them takes it out of the group. */
export function detectionsForMember<T extends DuplicatePair>(group: DuplicateGroup<T>, memberId: number): T[] {
  return group.detections.filter((d) => d.idea_id === memberId || d.duplicate_of_id === memberId);
}

/**
 * Per-section counts for the Review view and its sidebar badge, so the
 * header numbers always add up to the badge. Duplicates count once per group.
 */
export function reviewCounts(
  queue: {
    duplicates: DuplicatePair[];
    connections: { id: number }[];
    stale: { id: number }[];
    code?: { id: number }[];
    suggested?: { id: number }[];
    code_links?: { id: number }[];
    stalled?: { id: number }[];
  } | null,
  done: ReadonlySet<string> = new Set()
): { duplicates: number; connections: number; stale: number; code: number; suggested: number; codeLinks: number; stalled: number; total: number } {
  if (!queue) return { duplicates: 0, connections: 0, stale: 0, code: 0, suggested: 0, codeLinks: 0, stalled: 0, total: 0 };
  const duplicates = groupDuplicates(queue.duplicates.filter((d) => !done.has(`dup-${d.id}`))).length;
  const connections = queue.connections.filter((c) => !done.has(`conn-${c.id}`)).length;
  const stale = queue.stale.filter((s) => !done.has(`stale-${s.id}`)).length;
  const code = (queue.code ?? []).filter((s) => !done.has(`code-${s.id}`)).length;
  const suggested = (queue.suggested ?? []).filter((s) => !done.has(`sug-${s.id}`)).length;
  const codeLinks = (queue.code_links ?? []).filter((s) => !done.has(`link-${s.id}`)).length;
  const stalled = (queue.stalled ?? []).filter((s) => !done.has(`stall-${s.id}`)).length;
  return {
    duplicates, connections, stale, code, suggested, codeLinks, stalled,
    total: duplicates + connections + stale + code + suggested + codeLinks + stalled,
  };
}
